import React, { type ReactElement, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import AppliedFilters from 'atom-components/applied-filters-lite';
import MultiComboBox, {
  type ComboBoxItem
} from 'atom-components/form-fields-for-hook-form/multi-combo-box';
import Label from 'atom-components/label';
import MarkStatusBadge from 'feature-components/badges/mark-status-badge';
import { markStatusDisplayName } from 'pages/marking/helpers/presentation.helpers';
import type { YesNoOrBothValue } from 'schemas/yes-no-or-both.schema';
import { MarkStatus } from 'services/assist-api/entities/test-instance-response-mark/types';
import { filterFormDefaultValues } from './filter-form.schema';

const fieldLabel = 'Status(es)';
const fieldName = 'statuses';

type Props = {
  triggerFormSubmission: () => void;
};

export default function StatusFilter({ triggerFormSubmission }: Props): ReactElement {
  const { control, setValue, watch, resetField } = useFormContext();
  const markingStarted: YesNoOrBothValue = watch('markingStarted');
  const reviewState: YesNoOrBothValue = watch('review');

  const selectableStatuses: ComboBoxItem[] = Object.values(MarkStatus)
    .filter((status) => status !== MarkStatus.Assigned)
    .map((status) => ({
      id: status.toString(),
      name: markStatusDisplayName(status),
      badge: <MarkStatusBadge>{status}</MarkStatusBadge>
    }));

  const isMarkingStartedNoApplied = markingStarted === 'N';
  const isReviewFilterApplied = ['Y', 'N'].includes(reviewState);
  useEffect(() => {
    if (isMarkingStartedNoApplied || isReviewFilterApplied) {
      resetField(fieldName, { defaultValue: filterFormDefaultValues.statuses });
    }
  }, [resetField, isReviewFilterApplied, isMarkingStartedNoApplied]);

  return (
    <div>
      <Label htmlFor={fieldName}>{fieldLabel}</Label>
      <MultiComboBox
        id={fieldName}
        name={fieldName}
        control={control}
        allItems={selectableStatuses}
        clearable
        disabled={isMarkingStartedNoApplied || isReviewFilterApplied}
        onChange={(items): void => {
          const statuses = items.map((item) => item.id);
          setValue(fieldName, statuses, {
            shouldValidate: true,
            shouldTouch: true
          });
          triggerFormSubmission();
        }}
      />
    </div>
  );
}

export function StatusAppliedFilter({ triggerFormSubmission }): ReactElement | null {
  const { watch, setValue } = useFormContext();
  const statuses: MarkStatus[] = watch(fieldName);

  if (statuses.length === 0) return null;

  return (
    <AppliedFilters.Filter name={fieldLabel}>
      {statuses.map(
        (status, index): ReactElement => (
          <AppliedFilters.FilterItem
            key={status}
            onClick={(): void => {
              const statusesCopy = [...statuses]; // Todo: Not needed once we change to toSpliced
              statusesCopy.splice(index, 1);
              setValue(fieldName, statusesCopy);
              triggerFormSubmission();
            }}
            hasBadge
          >
            <MarkStatusBadge>{status}</MarkStatusBadge>
          </AppliedFilters.FilterItem>
        )
      )}
    </AppliedFilters.Filter>
  );
}
